// `WordOutline` (SuiteImport/WordImporter.swift), ported step for step: how a Word document's paragraphs become a binder.
//
// Heading styles (or any style with an outline level) make the structure. Top-level headings are chapters, or parts when
// the headings under them look like chapters ("Part Two", "Chapter 3"). A chapter without headings under it becomes a
// folder of scenes split at its scene breaks; headings inside a chapter are its scenes, named by the heading. A chapter
// number on a line of its own just before a heading ("1", "Chapter One") is left out, as compile numbers chapters.
// Headings named like book matter (Dedication, Acknowledgements…) fill those documents; the text before the first heading
// is the title page (and copyright, from a line with ©). Without headings, "Chapter …" or number lines make chapters if
// there are several in order; otherwise the whole text is one scene.
import type { Inline, InlineStyle } from '../contract.ts';
import { plainStyle } from '../proseMarkdown.ts';
import { BookMatter } from '../bookMatter.ts';
import { charIsLetter, charIsNumber, charIsWhitespace, graphemes, trimCFWhitespace } from '../unicode.ts';
import { AnchorSentinels } from './anchorSentinels.ts';
import type { WordDocument, WordParagraph } from './wordDocument.ts';

export type WordSectionType = 'Part' | 'Chapter' | 'Scene';
export const WORD_SECTION_TYPES: readonly WordSectionType[] = ['Part', 'Chapter', 'Scene'];
export const sectionOptionID = (type: WordSectionType) => `section-${type.toLowerCase()}`;

export interface WordNode {
  title: string;
  isFolder: boolean;
  sectionType: WordSectionType | null;
  paragraphs: WordParagraph[];
  children: WordNode[];
}

export interface WordOutline {
  title: string;
  nodes: WordNode[];
  /** Book-matter documents to fill, in the order found. */
  matter: [BookMatter, WordParagraph[]][];
  /** What was made of the document, for the import report. */
  summary: string[];
  issues: string[];
  /** Words in the document's paragraphs (headings included, contents lists excluded), to check the import against. */
  sourceWords: number;
  /** Words of headings that became titles rather than text. */
  titleWords: number;
}

export const headingLevel = (p: WordParagraph): number | null => (p.role === 'heading' ? p.level : null);

/** `WordOutline.build`. */
export function buildWordOutline(doc: WordDocument, filename: string): WordOutline {
  let paragraphs = doc.paragraphs.map((p) => ({ ...p }));
  const summary: string[] = [];
  const issues: string[] = [];
  const sourceWords = paragraphs.filter((p) => p.role !== 'sceneBreak').reduce((n, p) => n + wordCountOf(p.text), 0);

  let firstHeading = paragraphs.findIndex((p) => headingLevel(p) !== null);
  if (firstHeading < 0) firstHeading = paragraphs.length;
  const titleParagraph = paragraphs.slice(0, firstHeading).find((p) => p.role === 'title');
  const title = [titleParagraph?.text, doc.title, filename].filter((t): t is string => typeof t === 'string')
    .map((t) => splitWhitespace(t).join(' ')).find((t) => t !== '') ?? 'Untitled';
  for (const p of paragraphs) if (p.role === 'title') p.role = 'body';

  let promoted = false;
  if (paragraphs.every((p) => headingLevel(p) === null)) promoted = promoteChapterLines(paragraphs);
  if (promoted) summary.push('No heading styles: chapters start at the “Chapter …” or number lines');

  // A chapter number on its own line just before a heading is left out: compile numbers chapters.
  const folded: WordParagraph[] = [];
  let numberWords = 0;
  for (const p of paragraphs) {
    const last = folded[folded.length - 1];
    if (headingLevel(p) !== null && last && last.role === 'body' && !last.images.length
      && graphemes(last.text).length <= 20 && chapterNumber(last.text) !== null) {
      folded.pop();
      numberWords += wordCountOf(last.text);
      folded.push({ ...p, inlines: [...last.inlines.filter(isMarker), ...p.inlines] });
      continue;
    }
    folded.push(p);
  }
  // A heading broken over lines ("Chapter One", "The Storm") becomes a one-line title.
  paragraphs = folded.map((p) => {
    if (headingLevel(p) === null) return p;
    return { ...p, text: p.text.split('\n').map((l) => trimCFWhitespace(l)).filter((l) => l !== '').join(' — ') };
  });

  // Headings → sections; book-matter headings → matter.
  const contentLevels = paragraphs.flatMap((p) => { const l = headingLevel(p); return l !== null && matterKind(p.text) === null ? [l] : []; });
  const matterLimit = (contentLevels.length ? Math.min(...contentLevels) : 0) + 1;
  interface Section { heading: WordParagraph; level: number; paragraphs: WordParagraph[]; children: Section[] }
  const preamble: WordParagraph[] = [];
  const roots: Section[] = [];
  const stack: Section[] = [];
  const matter: [BookMatter, WordParagraph[]][] = [];
  const matterMarkers: Inline[][] = [];
  let inMatter = false;
  let titleWords = numberWords;
  const headingStyles = new Map<number, Set<string>>();
  for (const p of paragraphs) {
    const level = headingLevel(p);
    if (level !== null) {
      titleWords += wordCountOf(p.text);
      const kind = level <= matterLimit ? matterKind(p.text) : null;
      if (kind) {
        matter.push([kind, []]);
        matterMarkers.push(p.inlines.filter(isMarker));
        inMatter = true;
        continue;
      }
      inMatter = false;
      if (!headingStyles.has(level)) headingStyles.set(level, new Set());
      headingStyles.get(level)!.add(p.styleName);
      // Parts sit above chapters at the same heading level.
      const section: Section = { heading: p, level: level * 2 - (isPartLike(p.text) ? 1 : 0), paragraphs: [], children: [] };
      while (stack.length && stack[stack.length - 1].level >= section.level) stack.pop();
      if (stack.length) stack[stack.length - 1].children.push(section);
      else roots.push(section);
      stack.push(section);
    } else if (inMatter) {
      matter[matter.length - 1][1].push(p);
    } else if (stack.length) {
      stack[stack.length - 1].paragraphs.push(p);
    } else {
      preamble.push(p);
    }
  }

  matter.forEach((entry, i) => {
    if (!attach(matterMarkers[i], entry[1])) issues.push(`A comment on the heading “${BookMatter.title(entry[0])}” had no text to attach to`);
  });

  let nodes: WordNode[] = [];
  if (!roots.length) {
    nodes = [{ title, isFolder: false, sectionType: null, paragraphs: preamble, children: [] }];
    if (!summary.length) summary.push('No headings: the whole text is one scene');
  } else {
    // Chapters are headings at the top level or inside a part. A chapter with no headings under it is split into scenes
    // at its scene breaks; headings inside a chapter are its scenes, and keep their scene breaks.
    let splitChapters = 0;
    const node = (s: Section, inChapter: boolean): WordNode => {
      // A comment on the heading moves to the section's first paragraph.
      const paragraphs = [...s.paragraphs];
      if (!attach(s.heading.inlines.filter(isMarker), paragraphs)) issues.push(`A comment on the heading “${s.heading.text}” had no text to attach to`);
      const title = s.heading.text;
      if (inChapter) {
        return { title, isFolder: s.children.length > 0, sectionType: 'Scene', paragraphs, children: s.children.map((c) => node(c, true)) };
      }
      if (s.children.length) {
        const isPart = isPartLike(title) || s.children.some((c) => chapterLike(c.heading.text));
        return { title, isFolder: true, sectionType: isPart ? 'Part' : 'Chapter', paragraphs, children: s.children.map((c) => node(c, !isPart)) };
      }
      const scenes = splitScenes(paragraphs);
      if (scenes.length > 1) splitChapters += 1;
      return { title, isFolder: true, sectionType: 'Chapter', paragraphs: [], children: scenes.map((scene, n) => (
        { title: `Scene ${n + 1}`, isFolder: false, sectionType: 'Scene', paragraphs: scene, children: [] })) };
    };
    nodes = roots.map((r) => node(r, false));

    const count = (type: WordSectionType, list: WordNode[]): number => list.reduce((n, x) => n + (x.sectionType === type ? 1 : 0) + count(type, x.children), 0);
    const counts = WORD_SECTION_TYPES.flatMap((type) => {
      const n = count(type, nodes);
      return n === 0 ? [] : [`${n} ${type.toLowerCase()}${n === 1 ? '' : 's'}`];
    });
    const styles = [...headingStyles.keys()].sort((a, b) => a - b).flatMap((l) => [...headingStyles.get(l)!].sort(compareStrings));
    if (numberWords > 0) summary.push('Chapter numbers on a line of their own before the heading were left out: compile numbers chapters');
    summary.push(`Structure: ${counts.join(', ')}` + (promoted ? '' : ` from ${styles.join(', ')}`)
      + (splitChapters > 0 ? '; chapters split into scenes at scene breaks' : ''));

    // Before the first heading: the title page, and the copyright notice from a line with ©.
    if (preamble.length) {
      const copyright = preamble.findIndex((p) => p.text.includes('©') || p.text.toLowerCase().startsWith('copyright'));
      if (copyright >= 0) {
        const titlePage = preamble.slice(0, copyright);
        if (titlePage.length) matter.unshift(['title-page', titlePage]);
        matter.splice(titlePage.length ? 1 : 0, 0, ['copyright', preamble.slice(copyright)]);
        summary.push('The text before the first heading is in Front Matter (Title Page and Copyright)');
      } else if (preamble.reduce((n, p) => n + wordCountOf(p.text), 0) < 250) {
        matter.unshift(['title-page', preamble]);
        summary.push('The text before the first heading is in Front Matter ▸ Title Page');
      } else {
        nodes.unshift({ title: 'Opening', isFolder: false, sectionType: null, paragraphs: preamble, children: [] });
        summary.push('The text before the first heading is the manuscript\'s first document, “Opening”');
      }
    }
  }
  const found = matter.map(([kind]) => kind).filter((k) => k !== 'title-page' || preamble.length > 0);
  if (found.length) summary.push(`Front and back matter: ${[...new Set(found)].map((k) => BookMatter.title(k)).join(', ')}`);
  return { title, nodes, matter, summary, issues, sourceWords, titleWords };
}

/** Swift's `String <`: by Unicode scalars (the same as UTF-16 order outside the astral planes). */
function compareStrings(a: string, b: string): number {
  const x = [...a].map((c) => c.codePointAt(0)!), y = [...b].map((c) => c.codePointAt(0)!);
  for (let i = 0; i < Math.min(x.length, y.length); i++) if (x[i] !== y[i]) return x[i] - y[i];
  return x.length - y.length;
}

/** `split(whereSeparator: \.isWhitespace)`, by Character. */
function splitWhitespace(text: string): string[] {
  const out: string[] = [];
  let current = '';
  for (const c of graphemes(text)) {
    if (charIsWhitespace(c)) { if (current) out.push(current); current = ''; } else current += c;
  }
  if (current) out.push(current);
  return out;
}

// MARK: Recognising text

/** `WordOutline.words`: whitespace-separated pieces with a letter or a digit in them, by Character. */
export function wordCountOf(text: string): number {
  return splitWhitespace(text).filter((w) => graphemes(w).some((c) => charIsLetter(c) || charIsNumber(c))).length;
}

export function splitScenes(paragraphs: WordParagraph[]): WordParagraph[][] {
  const scenes: WordParagraph[][] = [[]];
  for (const p of paragraphs) {
    if (p.role === 'sceneBreak') scenes.push([]);
    else scenes[scenes.length - 1].push(p);
  }
  return scenes.filter((scene) => scene.some((p) => p.text !== '' || p.images.length > 0));
}

const NUMBER_WORDS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen',
  'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS: Record<string, number> = { twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };

/** `trimmingCharacters(in: .whitespaces.union(".:"))`. */
function trimNumberLine(s: string): string {
  const keep = (c: string) => c !== '.' && c !== ':' && trimCFWhitespace(c) !== '';
  const chars = [...s];
  let a = 0, b = chars.length;
  while (a < b && !keep(chars[a])) a++;
  while (b > a && !keep(chars[b - 1])) b--;
  return chars.slice(a, b).join('');
}

/** The value of "12", "XII", "Twelve", "Twenty-One", or "Chapter 12" (and so on), else null. A bare Roman numeral counts
 *  only in capitals ("XII"; "Vivid." or "Did." is a word); after "Chapter" (`afterChapter`, or in `text`) any case does. */
export function chapterNumber(text: string, afterChapter = false): number | null {
  let original = trimNumberLine(text);
  let named = afterChapter;
  if (original.toLowerCase().startsWith('chapter ')) {
    original = trimCFWhitespace([...original].slice(8).join(''));
    named = true;
  }
  const s = original.toLowerCase();
  if (/^[+-]?[0-9]+$/.test(s)) {
    const n = Number(s);
    if (n > 0 && n < 1000) return n;
  }
  if (named || original === original.toUpperCase()) {
    const n = romanValue(s);
    if (n !== null) return n;
  }
  const parts = s.split(/[- ]/).filter(Boolean);
  if (parts.length === 1) {
    const i = NUMBER_WORDS.indexOf(parts[0]);
    if (i >= 0) return i + 1;
  }
  const t = parts.length && Object.prototype.hasOwnProperty.call(TENS, parts[0]) ? TENS[parts[0]] : undefined;
  if (t !== undefined) {
    if (parts.length === 1) return t;
    if (parts.length === 2) {
      const i = NUMBER_WORDS.slice(0, 9).indexOf(parts[1]);
      if (i >= 0) return t + i + 1;
    }
  }
  return null;
}

/** A canonical lower-case Roman numeral's value (so "ill", "mild", "civil" aren't numbers). */
export function romanValue(s: string): number | null {
  const values: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
  if (!s || !/^m{0,3}(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})$/.test(s)) return null;
  const digits = [...s].map((c) => values[c]);
  let total = 0;
  digits.forEach((d, i) => { total += i + 1 < digits.length && d < digits[i + 1] ? -d : d; });
  return total;
}

/** Characters of Unicode's punctuation categories (Foundation's `.punctuationCharacters`). */
const trimPunctuation = (s: string) => s.replace(/^\p{P}+|\p{P}+$/gu, '');
const chapterSeparators = /[:. —–]/;

/** The first piece of what follows "Chapter " (split at colons, full stops, spaces and dashes). */
function afterChapterWord(text: string): string | undefined {
  return [...text].slice(8).join('').split(chapterSeparators).find((piece) => piece !== '');
}

/** "Chapter 3", "Chapter Three: The Storm", "3", "Prologue". */
export function chapterLike(text: string): boolean {
  const lower = text.toLowerCase();
  if (['prologue', 'epilogue'].includes(trimPunctuation(lower))) return true;
  if (chapterNumber(text) !== null) return true;
  if (!lower.startsWith('chapter ')) return false;
  const piece = afterChapterWord(text) ?? afterChapterWord(lower);
  return piece !== undefined && chapterNumber(piece, true) !== null;
}

export function isPartLike(text: string): boolean {
  return /^(part|book|volume|act)\s+\S+.*$/u.test(text.toLowerCase());
}

/** Without heading styles: several "Chapter …" or number lines, in order, become headings. */
export function promoteChapterLines(paragraphs: WordParagraph[]): boolean {
  const candidates = paragraphs.flatMap((p, i) =>
    p.role === 'body' && graphemes(p.text).length <= 80 && !p.text.includes('\n') && chapterLike(p.text) ? [i] : []);
  const numbered = candidates.flatMap((i) => {
    const text = paragraphs[i].text;
    let n: number | null;
    if (text.toLowerCase().startsWith('chapter ')) {
      const token = [...text].slice(8).join('').split(/[:. ]/).find((piece) => piece !== '') ?? '';
      n = chapterNumber(token, true);
    } else {
      n = chapterNumber(text);
    }
    return n === null ? [] : [n];
  });
  if (numbered.length < 2 || !numbered.every((n, k) => k === 0 || numbered[k - 1] < n)) return false;
  for (const i of candidates) { paragraphs[i].role = 'heading'; paragraphs[i].level = 0; }
  return true;
}

export function matterKind(title: string): BookMatter | null {
  const key = graphemes(title.toLowerCase()).filter(charIsLetter).join('');
  switch (key) {
    case 'dedication': return 'dedication';
    case 'epigraph': return 'epigraph';
    case 'copyright': case 'copyrightpage': return 'copyright';
    case 'contents': case 'tableofcontents': return 'contents';
    case 'titlepage': return 'title-page';
    case 'halftitle': case 'halftitlepage': return 'half-title';
    case 'acknowledgements': case 'acknowledgments': return 'acknowledgements';
    case 'abouttheauthor': return 'about-the-author';
    case 'authorsnote': case 'notefromtheauthor': case 'anotefromtheauthor': return 'authors-note';
    default: return key.startsWith('alsoby') || key.startsWith('otherbooksby') ? 'also-by' : null;
  }
}

/** A heading's anchor markers move onto the first paragraph with text: starts before, ends after. Changes `paragraphs`
 *  in place (the paragraph is replaced, never edited). */
export function attach(markers: Inline[], paragraphs: WordParagraph[]): boolean {
  if (!markers.length) return true;
  const i = paragraphs.findIndex((p) => p.text !== '');
  if (i < 0) return false;
  // Markers take the style of the text they now sit beside, so they don't open emphasis.
  const styles = paragraphs[i].inlines.flatMap((inline) => (inline.type === 'text' && !isMarkerText(inline.text) ? [inline.style] : []));
  const restyled = (kind: string, style: InlineStyle | undefined): Inline[] =>
    markers.flatMap((m): Inline[] => (m.type === 'text' && m.text.startsWith(kind) ? [{ type: 'text', text: m.text, style: style ?? plainStyle() }] : []));
  paragraphs[i] = { ...paragraphs[i], inlines: [...restyled(AnchorSentinels.start, styles[0]), ...paragraphs[i].inlines, ...restyled(AnchorSentinels.end, styles[styles.length - 1])] };
  return true;
}

/** `AnchorSentinels.isMarker`: text starting with a marker's first scalar. */
export function isMarkerText(s: string): boolean {
  const first = s.codePointAt(0);
  return first === 0xe000 || first === 0xe001;
}

export const isMarker = (inline: Inline): boolean => inline.type === 'text' && isMarkerText(inline.text);
